import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { router } from './router';
import { useTheme } from '@/hooks/useTheme';
import { makeMuiTheme } from '@/theme/muiTheme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export default function App() {
  const { theme } = useTheme();

  // Apply theme class to <html> on mount and whenever it changes.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Thin placeholder MUI theme, tracked to the SPA's light/dark toggle so DataGridPro matches
  // the page mode. StyledEngineProvider injectFirst + NO CssBaseline lets MUI coexist with the
  // Tailwind/Shadcn styles unchanged — see src/theme/muiTheme.ts for the rationale.
  const muiTheme = useMemo(() => makeMuiTheme(theme), [theme]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={muiTheme}>
        {/* date-fns adapter powers the MUI X date-range pickers (Wave 2B). */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
